import type { ChainAddress } from '@/utils/models';

/** Severity attached directly to every typed validation result. */
export type ValidationSeverity = 'blocking' | 'warning';

/** Mode-agnostic, code-discriminated validation outcomes that any trade-mode validator can emit. */
export type CommonValidationResult =
  | {
      readonly code: 'rwa-geo-blocked';
      readonly severity: 'blocking';
      readonly blockedTokenAddresses: readonly ChainAddress[];
    }
  | { readonly code: 'buy-token-blocked'; readonly severity: 'blocking' }
  | { readonly code: 'sell-token-blocked'; readonly severity: 'blocking' }
  | { readonly code: 'buy-token-required'; readonly severity: 'blocking' }
  | { readonly code: 'sell-token-required'; readonly severity: 'blocking' }
  | {
      readonly code: 'cross-chain-unsupported';
      readonly severity: 'blocking';
      readonly sellTokenChainId: number;
      readonly buyTokenChainId: number;
    }
  | { readonly code: 'insufficient-balance'; readonly severity: 'blocking' }
  | { readonly code: 'token-needs-approval'; readonly severity: 'blocking' }
  | {
      readonly code: 'high-price-impact';
      readonly severity: 'warning';
      readonly priceImpactPercent: number;
    };

/**
 * Pre-classified quote failures accepted by the validation boundary.
 *
 * Classified codes fold into their matching results (`buy-token-blocked`,
 * `sell-token-blocked`, `insufficient-balance`) instead of surfacing as
 * `quote-failed`; `unknown` is reserved for failures the derivation layer
 * cannot classify.
 */
export type QuoteErrorInput =
  | { readonly code: 'sell-amount-too-small' }
  | { readonly code: 'buy-token-unauthorized' }
  | { readonly code: 'sell-token-unauthorized' }
  | { readonly code: 'insufficient-balance' }
  | { readonly code: 'unknown'; readonly error: Error };

/** Mode-agnostic normalized facts shared by every trade-mode validator. */
export type CommonValidationInput = {
  readonly sellTokenAddress: ChainAddress | undefined;
  readonly buyTokenAddress: ChainAddress | undefined;
  readonly sellTokenChainId: number | undefined;
  readonly buyTokenChainId: number | undefined;
  readonly isSellTokenBlocked: boolean;
  readonly isBuyTokenBlocked: boolean;
  readonly rwaBlockedTokenAddresses: readonly ChainAddress[];
  readonly checksApprovalBeforeBalance: boolean;
  readonly priceImpactPercent: number | undefined;
};

/** Preserves the inclusive 3% same-chain warning; cross-chain keeps its separate threshold. */
export const HIGH_PRICE_IMPACT_WARNING_PERCENT = 3;

/** Appends RWA, buy, and sell token restriction blockers in legacy-primary order. */
export function appendTokenRestrictionResults<Result>(
  input: Pick<
    CommonValidationInput,
    'rwaBlockedTokenAddresses' | 'isBuyTokenBlocked' | 'isSellTokenBlocked'
  >,
  results: Array<Result | CommonValidationResult>,
): void {
  if (input.rwaBlockedTokenAddresses.length > 0) {
    results.push({
      code: 'rwa-geo-blocked',
      severity: 'blocking',
      blockedTokenAddresses: input.rwaBlockedTokenAddresses,
    });
  }
  if (input.isBuyTokenBlocked) {
    results.push({ code: 'buy-token-blocked', severity: 'blocking' });
  }
  if (input.isSellTokenBlocked) {
    results.push({ code: 'sell-token-blocked', severity: 'blocking' });
  }
}

/** Appends blockers for whichever trade side has no token selected. */
export function appendRequiredTokenResults<Result>(
  input: CommonValidationInput,
  results: Array<Result | CommonValidationResult>,
): void {
  if (input.buyTokenAddress === undefined) {
    results.push({ code: 'buy-token-required', severity: 'blocking' });
  }
  if (input.sellTokenAddress === undefined) {
    results.push({ code: 'sell-token-required', severity: 'blocking' });
  }
}

/** Reports whether both trade sides resolve to the same chain. */
export function isSameChainPair(input: CommonValidationInput): boolean {
  return (
    input.sellTokenChainId !== undefined &&
    input.buyTokenChainId !== undefined &&
    input.sellTokenChainId === input.buyTokenChainId
  );
}

/** Appends the same-chain high price impact warning at the inclusive threshold. */
export function appendHighPriceImpactResult<Result>(
  input: CommonValidationInput,
  results: Array<Result | CommonValidationResult>,
): void {
  if (
    isSameChainPair(input) &&
    input.priceImpactPercent !== undefined &&
    input.priceImpactPercent <= -HIGH_PRICE_IMPACT_WARNING_PERCENT
  ) {
    results.push({
      code: 'high-price-impact',
      severity: 'warning',
      priceImpactPercent: input.priceImpactPercent,
    });
  }
}

/** Narrows a result to variants whose literal severity blocks submission. */
export function isBlockingResult<Result extends { readonly severity: ValidationSeverity }>(
  result: Result,
): result is Extract<Result, { readonly severity: 'blocking' }> {
  return result.severity === 'blocking';
}

/** Reports whether any ordered validation result blocks submission. */
export function hasBlockingResult(
  results: readonly { readonly severity: ValidationSeverity }[],
): boolean {
  return results.some(isBlockingResult);
}

/** Returns the first result, which preserves the legacy primary precedence. */
export function getPrimaryValidationResult<
  Result extends { readonly severity: ValidationSeverity },
>(results: readonly Result[]): Result | undefined {
  return results[0];
}
